import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { OrgConfigurationKey, useOrgConfiguration } from '@src/providers/OrgConfigurationProvider';
import {
  BlotterDensity,
  DEFAULT_MAX_ROWS,
  TRADE,
  removeEmptyFilters,
  useConstant,
  usePersistedBlotterTable,
  useTradeColumns,
  type Column,
  type Trade,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { v1 as uuid } from 'uuid';
import { usePredictionMarketBlotterColumns } from '../predictionMarketBlotter';
import { DEFAULT_TRADE_COLUMNS } from './columns';
import { toTradesServerFilter } from './serverFilter';
import {
  useTradeFilter,
  type BlotterTableTradeFilter,
  type TradeAdditionalFilterState,
  type UseTradeFilterOutput,
} from './useTradeFilter';

interface UseFilteredTradesBlotterParams {
  blotterPersistID: string;
  defaultFilter: BlotterTableTradeFilter;
  defaultColumns?: (keyof Trade | Partial<Column>)[];
  orderID?: string;
  persistFilter?: boolean;
  filterByOrderID: boolean;
  additionalFilterState: TradeAdditionalFilterState;
  blotterTagPrefix: string;
}

interface UseFilteredTradesBlotterResult {
  density: BlotterDensity;
  rowID: 'TradeID';
  columns: ReturnType<typeof useTradeColumns>;
  persistedBlotterTable: ReturnType<typeof usePersistedBlotterTable>;
  filteredTrades: UseTradeFilterOutput<Trade>;
  request: {
    name: typeof TRADE;
    tag: string;
    sort_by: '-TransactTime';
    OrderID?: string;
    ExcludeUsers?: string[];
  } & Partial<BlotterTableTradeFilter>;
  startingRowLimit: number;
}

export function useFilteredTradesBlotter({
  blotterPersistID,
  defaultFilter,
  defaultColumns,
  orderID,
  persistFilter,
  filterByOrderID,
  additionalFilterState,
  blotterTagPrefix,
}: UseFilteredTradesBlotterParams): UseFilteredTradesBlotterResult {
  const { getConfig } = useOrgConfiguration();
  const { enableSEDOLDisplay } = useFeatureFlag();

  const tradeColumns = useTradeColumns({
    defaultColumns: defaultColumns ?? DEFAULT_TRADE_COLUMNS,
    includeSedol: enableSEDOLDisplay,
  });
  const predictionColumns = usePredictionMarketBlotterColumns();
  const columns = useMemo(() => [...tradeColumns, ...predictionColumns], [predictionColumns, tradeColumns]);

  const persistedBlotterTable = usePersistedBlotterTable(
    blotterPersistID,
    { filter: defaultFilter },
    {
      persistFilter,
    }
  );

  const enableTradesUserBackendFiltering = getConfig(OrgConfigurationKey.EnableTradesUserBackendFiltering, false);

  const filteredTrades = useTradeFilter<Trade>({
    persistedBlotterTable,
    filterByOrderID,
    additionalFilterState,
    defaultMultilegView: defaultFilter.MultilegView,
    enableTradesUserBackendFiltering,
  });

  const blotterTag = useConstant(`${blotterTagPrefix}_${uuid()}`);

  const request = useMemo<UseFilteredTradesBlotterResult['request']>(
    () => ({
      name: TRADE as UseFilteredTradesBlotterResult['request']['name'],
      tag: blotterTag,
      sort_by: '-TransactTime' as const,
      ...(orderID != null && { OrderID: orderID }),
      ...removeEmptyFilters(toTradesServerFilter(filteredTrades.filter, { enableTradesUserBackendFiltering })),
    }),
    [blotterTag, enableTradesUserBackendFiltering, orderID, filteredTrades.filter]
  );

  const startingRowLimit = getConfig(OrgConfigurationKey.TradeRowsMax, DEFAULT_MAX_ROWS);

  return {
    density: BlotterDensity.Compact,
    rowID: 'TradeID',
    columns,
    persistedBlotterTable,
    filteredTrades,
    request,
    startingRowLimit,
  };
}
